import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useTenant } from '../contexts/TenantContext';

/**
 * useTournaments — liste des tournois du tenant/agency.
 * R1 multi-tenant. prize_pool reste chiffré (non affiché en clair Phase 9).
 */
export function useTournaments() {
  const { tenantId, agencyId, loading: tenantLoading } = useTenant();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true); setError(null);
    try {
      let q = supabase
        .from('tournaments')
        .select('id, title, format, server, status, starts_at, ends_at, created_at')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (agencyId) q = q.eq('agency_id', agencyId);
      const { data, error: e1 } = await q.limit(1000);
      if (e1) throw e1;
      setRows(data || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, [tenantId, agencyId]);

  useEffect(() => { if (!tenantLoading) load(); }, [load, tenantLoading]);

  const create = useCallback(async ({ title, format, server, starts_at, ends_at }) => {
    if (!tenantId || !agencyId) throw new Error('Missing tenant/agency');
    const { data, error: e } = await supabase
      .from('tournaments')
      .insert({ tenant_id: tenantId, agency_id: agencyId, title, format, server, starts_at: starts_at || null, ends_at: ends_at || null })
      .select()
      .single();
    if (e) throw e;
    await load();
    return data;
  }, [tenantId, agencyId, load]);

  return { rows, loading: loading || tenantLoading, error, reload: load, create };
}

/**
 * useTournamentDetail — un tournoi + ses participants + matches.
 */
export function useTournamentDetail(tournamentId) {
  const { tenantId, loading: tenantLoading } = useTenant();
  const [tournament, setTournament] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId || !tournamentId) return;
    setLoading(true); setError(null);
    try {
      const [tRes, pRes, mRes] = await Promise.all([
        supabase.from('tournaments').select('*').eq('id', tournamentId).eq('tenant_id', tenantId).maybeSingle(),
        supabase.from('tournament_participants').select('id, creator_id, display_name, seed, status').eq('tournament_id', tournamentId).order('seed', { ascending: true }),
        supabase.from('tournament_matches').select('id, round, position, participant_a, participant_b, score_a, score_b, winner, status, scheduled_at').eq('tournament_id', tournamentId).order('round', { ascending: true }).order('position', { ascending: true }),
      ]);
      if (tRes.error) throw tRes.error;
      setTournament(tRes.data);
      setParticipants(pRes.data || []);
      setMatches(mRes.data || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, [tenantId, tournamentId]);

  useEffect(() => { if (!tenantLoading) load(); }, [load, tenantLoading]);

  return { tournament, participants, matches, loading: loading || tenantLoading, error, reload: load };
}
